/**
 * IELTS frontend API client — mirrors gmatApi.js. Completely separate cookie
 * (`ielts_session_token`) so it cannot collide with the GMAT session.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class HttpError extends Error {
  constructor(message, status, data) { super(message); this.status = status; this.data = data; }
}

const request = async (method, path, body, extraHeaders) => {
  const opts = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(extraHeaders || {}) },
  };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const r = await fetch(`${BACKEND_URL}${path}`, opts);
  let data = null;
  try { data = await r.json(); } catch (e) { /* tolerate non-JSON */ }
  if (!r.ok) throw new HttpError(data?.detail || r.statusText, r.status, data);
  return data;
};

export const ieltsApi = {
  authExchange: (sessionId) => request('POST', '/api/ielts/auth/exchange', undefined, { 'X-Session-ID': sessionId }),
  authMe:       () => request('GET', '/api/ielts/auth/me'),
  authLogout:   () => request('POST', '/api/ielts/auth/logout'),

  dashboard:    () => request('GET', '/api/ielts/dashboard'),

  practiceNext:   (body) => request('POST', '/api/ielts/practice/next', body),
  practiceSubmit: (body) => request('POST', '/api/ielts/practice/submit', body),

  mockAvailability: () => request('GET', '/api/ielts/mock/availability'),
  mockStart:    (body) => request('POST', '/api/ielts/mock/start', body),
  mockQuestion: (sid, qid) => request('GET', `/api/ielts/mock/${sid}/question/${qid}`),
  mockReadingPassage: (sid, pid) => request('GET', `/api/ielts/mock/${sid}/reading-passage/${pid}`),
  mockListeningSet: (sid, lid) => request('GET', `/api/ielts/mock/${sid}/listening-set/${lid}`),
  mockFinalize: (sid) => request('POST', `/api/ielts/mock/${sid}/finalize`),

  writingSubmitLegacy: (body) => request('POST', '/api/ielts/writing/submit', body),
  speakingSubmit: (body) => request('POST', '/api/ielts/speaking/submit', body),

  adminList:        (params) => request('GET', `/api/ielts/admin/questions${params || ''}`),
  adminCreate:      (body) => request('POST', '/api/ielts/admin/questions', body),
  adminUpdate:      (qid, body) => request('PATCH', `/api/ielts/admin/questions/${qid}`, body),
  adminDelete:      (qid) => request('DELETE', `/api/ielts/admin/questions/${qid}`),
  adminBulkUpload:  (questions) => request('POST', '/api/ielts/admin/questions/bulk-upload', { questions }),
  adminStudents:    () => request('GET', '/api/ielts/admin/students'),

  // Passage-based Reading (real-IELTS structure)
  readingNextPassage: (params) => request('GET', `/api/ielts/reading/next-passage${params || ''}`),
  readingSubmitBatch: (body) => request('POST', '/api/ielts/reading/submit-batch', body),

  // Set-based Listening (real-IELTS structure)
  listeningNextSet:    (params) => request('GET', `/api/ielts/listening/next-set${params || ''}`),
  listeningSubmitBatch: (body) => request('POST', '/api/ielts/listening/submit-batch', body),

  adminPassageList:   (params) => request('GET', `/api/ielts/admin/passages${params || ''}`),
  adminPassageGet:    (pid) => request('GET', `/api/ielts/admin/passages/${pid}`),
  adminPassageCreate: (body) => request('POST', '/api/ielts/admin/passages', body),
  adminPassageUpdate: (pid, body) => request('PATCH', `/api/ielts/admin/passages/${pid}`, body),
  adminPassageDelete: (pid) => request('DELETE', `/api/ielts/admin/passages/${pid}`),
  adminPassageBulk:   (passages) => request('POST', '/api/ielts/admin/passages/bulk-upload', { passages }),

  adminListeningList:   (params) => request('GET', `/api/ielts/admin/listening-sets${params || ''}`),
  adminListeningGet:    (sid) => request('GET', `/api/ielts/admin/listening-sets/${sid}`),
  adminListeningCreate: (body) => request('POST', '/api/ielts/admin/listening-sets', body),
  adminListeningUpdate: (sid, body) => request('PATCH', `/api/ielts/admin/listening-sets/${sid}`, body),
  adminListeningDelete: (sid) => request('DELETE', `/api/ielts/admin/listening-sets/${sid}`),
  adminListeningBulk:   (listening_sets) => request('POST', '/api/ielts/admin/listening-sets/bulk-upload', { listening_sets }),
  adminListeningBulkAudio: (items) => request('POST', '/api/ielts/admin/listening-sets/bulk-audio-urls', { items }),
  adminListeningExportTranscripts: (params) => request('GET', `/api/ielts/admin/listening-sets/export-transcripts${params || ''}`),

  // One-click LIVE seeding from bundled seed_data files (idempotent)
  adminSeedReadingFromBundle:   () => request('POST', '/api/ielts/admin/seed/reading-from-bundle'),
  adminSeedListeningFromBundle: () => request('POST', '/api/ielts/admin/seed/listening-from-bundle'),

  // ── Writing module ────────────────────────────────────────────────
  writingNextPrompt:   (params) => request('GET', `/api/ielts/writing/next-prompt${params || ''}`),
  writingSubmitEssay:  (body) => request('POST', '/api/ielts/writing/submit', body),
  writingListSubmissions: (limit = 30) => request('GET', `/api/ielts/writing/submissions?limit=${limit}`),
  writingGetSubmission: (sid) => request('GET', `/api/ielts/writing/submissions/${sid}`),
  writingEvaluateSubmission: (sid) => request('POST', `/api/ielts/writing/submissions/${sid}/evaluate`),
  writingDashboard:    () => request('GET', '/api/ielts/writing/dashboard'),
  writingMockStart:    (body) => request('POST', '/api/ielts/writing/mock/start', body),
  writingMockPrompt:   (msid, pid) => request('GET', `/api/ielts/writing/mock/${msid}/prompt/${pid}`),
  writingMockFinalize: (msid) => request('POST', `/api/ielts/writing/mock/${msid}/finalize`),

  // Writing admin
  adminWritingList:    (params) => request('GET', `/api/ielts/writing/admin/prompts${params || ''}`),
  adminWritingGet:     (pid) => request('GET', `/api/ielts/writing/admin/prompts/${pid}`),
  adminWritingCreate:  (body) => request('POST', '/api/ielts/writing/admin/prompts', body),
  adminWritingUpdate:  (pid, body) => request('PATCH', `/api/ielts/writing/admin/prompts/${pid}`, body),
  adminWritingDelete:  (pid) => request('DELETE', `/api/ielts/writing/admin/prompts/${pid}`),
  adminWritingBulk:    (prompts) => request('POST', '/api/ielts/writing/admin/prompts/bulk-upload', { prompts }),
  adminSeedWritingFromBundle: () => request('POST', '/api/ielts/writing/admin/seed/writing-from-bundle'),

  // ── Speaking module ──────────────────────────────────────────────
  speakingNextPrompt:   (params) => request('GET', `/api/ielts/speaking/next-prompt${params || ''}`),
  speakingSubmitResponse: (body) => request('POST', '/api/ielts/speaking/submit', body),
  speakingListSubmissions: (limit = 30) => request('GET', `/api/ielts/speaking/submissions?limit=${limit}`),
  speakingGetSubmission: (sid) => request('GET', `/api/ielts/speaking/submissions/${sid}`),
  speakingEvaluateSubmission: (sid) => request('POST', `/api/ielts/speaking/submissions/${sid}/evaluate`),
  speakingDashboard:    () => request('GET', '/api/ielts/speaking/dashboard'),
  speakingMockStart:    (body) => request('POST', '/api/ielts/speaking/mock/start', body),
  speakingMockPrompt:   (msid, pid) => request('GET', `/api/ielts/speaking/mock/${msid}/prompt/${pid}`),
  speakingMockFinalize: (msid) => request('POST', `/api/ielts/speaking/mock/${msid}/finalize`),

  // Speaking admin
  adminSpeakingList:    (params) => request('GET', `/api/ielts/speaking/admin/prompts${params || ''}`),
  adminSpeakingGet:     (pid) => request('GET', `/api/ielts/speaking/admin/prompts/${pid}`),
  adminSpeakingCreate:  (body) => request('POST', '/api/ielts/speaking/admin/prompts', body),
  adminSpeakingUpdate:  (pid, body) => request('PATCH', `/api/ielts/speaking/admin/prompts/${pid}`, body),
  adminSpeakingDelete:  (pid) => request('DELETE', `/api/ielts/speaking/admin/prompts/${pid}`),
  adminSpeakingBulk:    (prompts) => request('POST', '/api/ielts/speaking/admin/prompts/bulk-upload', { prompts }),
  adminSeedSpeakingFromBundle: () => request('POST', '/api/ielts/speaking/admin/seed/speaking-from-bundle'),
};

export const goToIeltsAuth = (nextPath) => {
  // Per Emergent Google Auth playbook — never hardcode the URL.
  const safeNext = typeof nextPath === 'string' && nextPath.startsWith('/') ? nextPath : '';
  if (safeNext) {
    try { sessionStorage.setItem('ielts_post_login_next', safeNext); } catch (e) { /* ignore */ }
  }
  const redirectUrl = `${window.location.origin}/ielts/profile${safeNext ? `?next=${encodeURIComponent(safeNext)}` : ''}`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};
